import React, { useMemo } from 'react';
import { Sample } from '../../../@types/samples/types';
import {
  buildLambdaSampleCurve,
  buildLiaoSampleCurve,
} from '../../../components/utils/curveGeneration';
import { AnalysisMethod, isKrMethod } from '../types';
import { getKrChartData } from '../krChartData';
import styles from '../MultiAnalysisPage.module.css';

const WIDTH = 240;
const HEIGHT = 150;
const MARGIN = { top: 6, right: 8, bottom: 16, left: 30 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;
const CURVE_POINTS = 30;

type Point = { x: number; y: number; selected?: boolean; outlier?: boolean };

/** Compact tick label: keep it short for a small chart. */
const fmt = (value: number): string => {
  if (!Number.isFinite(value)) return '';
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 1) return value.toFixed(1);
  if (abs === 0) return '0';
  return value.toFixed(2);
};

const collectPoints = (
  pcValues?: (number | null)[] | null,
  swValues?: (number | null)[] | null,
  selectedIndexes?: number[] | null,
  outlierIndexes?: number[] | null
): Point[] => {
  const points: Point[] = [];
  (pcValues ?? []).forEach((pc, index) => {
    const sw = swValues?.[index];
    if (pc != null && sw != null && pc > 0) {
      points.push({
        x: sw,
        y: pc,
        // null/undefined indexes = no selection info → all selected.
        selected: selectedIndexes ? selectedIndexes.includes(index) : true,
        outlier: outlierIndexes ? outlierIndexes.includes(index) : false,
      });
    }
  });
  return points;
};

/** Measured lab points: corrected Sw vs reservoir Pc from the active
 * methodology's curves, falling back to the other methodology's and then to
 * the raw pc_model curve. In lambda mode each point carries whether it was
 * part of the fitted subset (`lambda_curves.indexes`) and whether it was an
 * auto-excluded internal outlier (`outcome.internal_excluded_indexes`). */
const getMeasuredPoints = (sample: Sample, method: AnalysisMethod): Point[] => {
  const liao = sample.analysis_curves?.liao;
  const lambda = sample.analysis_curves?.lambda_curves;
  const outlierIndexes =
    method === 'lambda'
      ? sample.analysis_outcomes?.lambda?.internal_excluded_indexes ?? null
      : null;
  const ordered =
    method === 'lambda' ? [lambda, liao] : [liao, lambda];

  for (const curves of ordered) {
    if (!curves) continue;
    const isLambdaCurves = curves === lambda;
    const pcValues =
      'PceMatch' in curves
        ? curves.PceMatch ?? curves.Pres
        : curves.Pres;
    const points = collectPoints(
      pcValues,
      curves.CorrectedSw,
      method === 'lambda' && isLambdaCurves ? lambda?.indexes : null,
      method === 'lambda' && isLambdaCurves ? outlierIndexes : null
    );
    if (points.length > 0) return points;
  }

  const model = sample.input_sample_curves?.pc_model;
  return collectPoints(model?.pc, model?.sw);
};

/** Analytic fit curve for the active methodology. */
const getFitCurve = (sample: Sample, method: AnalysisMethod): Point[] => {
  if (method === 'lambda') {
    const lambda = sample.analysis_values?.lambda_values;
    if (!lambda) return [];
    // Prefer the backend-resolved swir/pc_max (swir falls back to the
    // minimum measured Sw when Swi is missing, so the curve still renders).
    const pres = sample.analysis_curves?.lambda_curves?.Pres ?? [];
    const derivedPcMax = pres.reduce<number>(
      (max, value) =>
        typeof value === 'number' && Number.isFinite(value) && value > max
          ? value
          : max,
      0
    );
    const measuredMax = getMeasuredPoints(sample, method).reduce(
      (max, point) => Math.max(max, point.y),
      0
    );
    const pcMax =
      lambda.pc_max ?? (derivedPcMax > 0 ? derivedPcMax : measuredMax);
    const swir = lambda.swir ?? sample.input_sample_values?.swi;
    return buildLambdaSampleCurve(
      { ...lambda, pc_max: pcMax },
      swir,
      CURVE_POINTS
    ).filter(point => point.y > 0);
  }

  const liao = sample.analysis_values?.liao;
  return liao
    ? buildLiaoSampleCurve(liao, CURVE_POINTS).filter(point => point.y > 0)
    : [];
};

/**
 * Lightweight SVG Pc-vs-Sw preview with axis scales: the active
 * methodology's fit line over the measured points, Pc on a log/linear Y axis
 * and Sw on a linear X axis. Pure SVG (no Highcharts per card) so hundreds of
 * thumbnails stay cheap. Keeps its aspect ratio so tick labels render
 * undistorted.
 */
type FitSparklineProps = {
  sample: Sample;
  /** Methodology whose fit curve is drawn. Defaults to Liao. */
  method?: AnalysisMethod;
  /** Y (Pc) axis scale. Defaults to log. */
  logScale?: boolean;
};

const FitSparkline = ({
  sample,
  method = 'liao',
  logScale = true,
}: FitSparklineProps) => {
  const chart = useMemo(() => {
    // Kr methodologies plot two curves (Kro + Krw/Krg) on a linear 0–1
    // Kr axis; the Pc methodologies keep the single log/linear Pc fit.
    const kr = isKrMethod(method) ? getKrChartData(sample, method) : null;
    const useLog = isKrMethod(method) ? false : logScale;
    const fits: Point[][] = kr
      ? [kr.curveDecreasing, kr.curveIncreasing]
      : [getFitCurve(sample, method)];
    const measured: Point[] = kr
      ? [...kr.measuredDecreasing, ...kr.measuredIncreasing]
      : getMeasuredPoints(sample, method);

    const positiveOnly = (p: Point) => !useLog || p.y > 0;
    const flatFits = fits.map(points => points.filter(positiveOnly));
    const shownMeasured = measured.filter(positiveOnly);
    const all = [...flatFits.flat(), ...shownMeasured];
    if (all.length === 0) return null;

    // X (saturation) is fixed 0–1 for every sample so thumbnails are
    // directly comparable. Y auto-scales to the data, log or linear.
    const toScale = (y: number) => (useLog ? Math.log10(y) : y);
    const fromScale = (v: number) => (useLog ? 10 ** v : v);
    const yScaled = all.map(p => toScale(p.y));
    const yMin = Math.min(...yScaled);
    const yMax = Math.max(...yScaled);
    const ySpan = yMax - yMin || 1;

    const toX = (x: number) => MARGIN.left + x * PLOT_W;
    const toY = (y: number) =>
      MARGIN.top + (1 - (toScale(y) - yMin) / ySpan) * PLOT_H;

    const fitPaths = flatFits
      .map(points =>
        [...points]
          .sort((a, b) => a.x - b.x)
          .map(
            (p, i) =>
              `${i === 0 ? 'M' : 'L'}${toX(p.x).toFixed(1)},${toY(p.y).toFixed(1)}`
          )
          .join(' ')
      )
      .filter(Boolean);

    const points = shownMeasured.map(p => ({
      cx: toX(p.x),
      cy: toY(p.y),
      selected: p.selected !== false,
      outlier: p.outlier === true,
    }));

    // X ticks: fixed 0 / 0.5 / 1 (Sw). Y ticks: min / mid / max on the axis.
    const xTicks = [0, 0.5, 1].map(value => ({ value, x: toX(value) }));
    const yTicks = [yMin, (yMin + yMax) / 2, yMax].map(scaled => {
      const value = fromScale(scaled);
      return { value, y: toY(value) };
    });

    return { fitPaths, points, xTicks, yTicks };
  }, [sample, method, logScale]);

  if (!chart) {
    return <div className={styles.sparklineEmpty}>No curve data</div>;
  }

  const axisBottom = MARGIN.top + PLOT_H;

  return (
    <svg
      className={styles.sparkline}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${
        method === 'lambda'
          ? 'Brooks-Corey'
          : method === 'water-oil'
          ? 'Water-Oil Kr'
          : method === 'gas-oil'
          ? 'Gas-Oil Kr'
          : 'Liao'
      } fit preview for ${sample.sample_code}`}
    >
      {/* gridlines + Y (Pc) tick labels */}
      {chart.yTicks.map((tick, index) => (
        <g key={`y-${index}`}>
          <line
            x1={MARGIN.left}
            x2={WIDTH - MARGIN.right}
            y1={tick.y}
            y2={tick.y}
            className={styles.sparklineGrid}
          />
          <text
            x={MARGIN.left - 3}
            y={tick.y}
            textAnchor="end"
            dominantBaseline="middle"
            className={styles.sparklineTick}
          >
            {fmt(tick.value)}
          </text>
        </g>
      ))}

      {/* X (Sw) tick labels */}
      {chart.xTicks.map((tick, index) => (
        <text
          key={`x-${index}`}
          x={tick.x}
          y={HEIGHT - 4}
          textAnchor={index === 0 ? 'start' : index === 2 ? 'end' : 'middle'}
          className={styles.sparklineTick}
        >
          {fmt(tick.value)}
        </text>
      ))}

      {/* axes */}
      <line
        x1={MARGIN.left}
        y1={MARGIN.top}
        x2={MARGIN.left}
        y2={axisBottom}
        className={styles.sparklineAxis}
      />
      <line
        x1={MARGIN.left}
        y1={axisBottom}
        x2={WIDTH - MARGIN.right}
        y2={axisBottom}
        className={styles.sparklineAxis}
      />

      {chart.points.map((point, index) => (
        <circle
          key={index}
          cx={point.cx}
          cy={point.cy}
          r={point.outlier ? 3 : 2.5}
          data-excluded={point.selected ? undefined : 'true'}
          data-outlier={point.outlier ? 'true' : undefined}
          className={
            point.outlier
              ? styles.sparklinePointOutlier
              : point.selected
              ? styles.sparklinePoint
              : styles.sparklinePointExcluded
          }
        />
      ))}
      {chart.fitPaths.map((path, index) => (
        <path
          key={`fit-${index}`}
          d={path}
          fill="none"
          className={styles.sparklineLine}
        />
      ))}
    </svg>
  );
};

export default FitSparkline;
